import React, { useState, useEffect } from 'react'
import { 
  DollarSign, Plus, Edit2, Trash2, Save, X,
  AlertTriangle, TrendingDown, Settings, Copy
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface Deduction {
  reason: string
  points: number
  category: string
}

const CATEGORIES = [
  { value: 'dishes', label: 'Dishes', color: 'bg-blue-500' },
  { value: 'trash', label: 'Trash', color: 'bg-gray-500' },
  { value: 'supplies', label: 'Supplies', color: 'bg-yellow-500' },
  { value: 'cleanliness', label: 'Cleanliness', color: 'bg-green-500' },
  { value: 'behavior', label: 'Behavior', color: 'bg-purple-500' },
  { value: 'screen', label: 'Screen Time', color: 'bg-indigo-500' },
  { value: 'food', label: 'Food', color: 'bg-orange-500' },
  { value: 'other', label: 'Other', color: 'bg-gray-500' }
]

export const DeductionEditor: React.FC = () => {
  const [deductions, setDeductions] = useState<Deduction[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddDeduction, setShowAddDeduction] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  
  const [newDeduction, setNewDeduction] = useState<Deduction>({
    reason: '',
    points: 5,
    category: 'behavior'
  })

  useEffect(() => {
    loadDeductions()
  }, [])

  const loadDeductions = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/deductions/common')
      setDeductions(data || [])
    } catch (error) {
      console.error('Failed to load deductions:', error)
      toast.error('Failed to load deductions')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateDeduction = async () => {
    if (!newDeduction.reason || !newDeduction.points) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await apiClient.post('/api/deductions/common', newDeduction)
      toast.success('Deduction created successfully')
      setShowAddDeduction(false)
      setNewDeduction({
        reason: '',
        points: 5,
        category: 'behavior'
      })
      loadDeductions()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to create deduction')
    }
  }

  const handleUpdateDeductions = async () => {
    try {
      await apiClient.put('/api/deductions/common', deductions)
      toast.success('Deductions updated successfully')
      setEditingIndex(null)
      loadDeductions()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to update deductions')
    }
  }

  const handleDeleteDeduction = async (reason: string) => {
    if (!confirm('Are you sure you want to delete this deduction? This cannot be undone.')) return

    try {
      await apiClient.delete(`/api/deductions/common/${encodeURIComponent(reason)}`)
      toast.success('Deduction deleted successfully')
      loadDeductions()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to delete deduction')
    }
  }

  const handleDuplicateDeduction = (deduction: Deduction) => {
    setNewDeduction({
      reason: `${deduction.reason} (Copy)`,
      points: deduction.points,
      category: deduction.category
    })
    setShowAddDeduction(true)
  }

  const filteredDeductions = deductions.filter(deduction => {
    const matchesSearch = deduction.reason.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || deduction.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  const getCategoryInfo = (category: string) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0]
  }


  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Deduction Management</h2>
        <p className="text-gray-600">Create and manage point deductions for infractions</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Search deductions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowAddDeduction(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Deduction
          </button>
        </div>
      </div>

      {/* Deductions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDeductions.map((deduction, index) => {
          const categoryInfo = getCategoryInfo(deduction.category)
          
          return (
            <div 
              key={index} 
              className="bg-white rounded-lg shadow p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full ${categoryInfo.color} mr-2`}></div>
                  <h3 className="font-semibold text-gray-900">{deduction.reason}</h3>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setEditingIndex(index)}
                    className="p-1 text-blue-600 hover:text-blue-900"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDuplicateDeduction(deduction)}
                    className="p-1 text-gray-600 hover:text-gray-900"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteDeduction(deduction.reason)}
                    className="p-1 text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-red-600">
                    -{deduction.points} pts
                  </span>
                  <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                    {categoryInfo.label}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add Deduction Modal */}
      {showAddDeduction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add New Deduction</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Reason *</label>
                <input
                  type="text"
                  value={newDeduction.reason}
                  onChange={(e) => setNewDeduction({ ...newDeduction, reason: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Left dishes in sink"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Points *</label>
                  <input
                    type="number"
                    min="1"
                    value={newDeduction.points}
                    onChange={(e) => setNewDeduction({ ...newDeduction, points: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <select
                    value={newDeduction.category}
                    onChange={(e) => setNewDeduction({ ...newDeduction, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddDeduction(false)
                  setNewDeduction({
                    reason: '',
                    points: 5,
                    category: 'behavior'
                  })
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDeduction}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Create Deduction
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Deduction Modal */}
      {editingIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Edit Deduction</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Reason</label>
                <input
                  type="text"
                  value={deductions[editingIndex].reason}
                  onChange={(e) => {
                    const updated = [...deductions]
                    updated[editingIndex] = { ...updated[editingIndex], reason: e.target.value }
                    setDeductions(updated)
                  }}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Points</label>
                  <input
                    type="number"
                    min="1"
                    value={deductions[editingIndex].points}
                    onChange={(e) => {
                      const updated = [...deductions]
                      updated[editingIndex] = { ...updated[editingIndex], points: parseInt(e.target.value) }
                      setDeductions(updated)
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    value={deductions[editingIndex].category}
                    onChange={(e) => {
                      const updated = [...deductions]
                      updated[editingIndex] = { ...updated[editingIndex], category: e.target.value }
                      setDeductions(updated)
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditingIndex(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateDeductions}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}